import React, { useState } from "react";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Users,
  Plus,
  Eye,
  Edit,
  Trash2,
  List,
  Search,
  X,
} from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { toast } from "sonner";
import { ScheduleProjectSheet } from "./ScheduleProjectSheet";
import { ProjectDetailsSheet } from "../clients/locations/ProjectDetailsSheet";
import { EditProjectSheet } from "../clients/locations/EditProjectSheet";
import { ProjectCalendarView } from "./ProjectCalendarView";
import { EmptyState } from "@/components/ui/empty-state";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const getStatusColor = (status: string) => {
  switch (status) {
    case "Scheduled":
      return "bg-blue-100 text-blue-800";
    case "In Progress":
      return "bg-yellow-100 text-yellow-800";
    case "Completed":
      return "bg-green-100 text-green-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export default function ProjectSchedulesPage() {
  const [isScheduleSheetOpen, setIsScheduleSheetOpen] = useState(false);
  const [isProjectDetailsSheetOpen, setIsProjectDetailsSheetOpen] = useState(false);
  const [isEditProjectSheetOpen, setIsEditProjectSheetOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [projectToDelete, setProjectToDelete] = useState<{ id: Id<"locationProjects">; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch all projects and current user
  const projects = useQuery(api.serviceLocations.getAllProjects) ?? [];
  const currentUser = useQuery(api.users.getCurrentUser);

  const canModifyProject = (project: any) => {
    if (!currentUser) return true;
    if (currentUser.appRole !== "member" && currentUser.appRole !== "manager") return true;
    return !project.createdBy || project.createdBy === currentUser._id;
  };

  // Delete project mutation
  const deleteProject = useMutation(api.serviceLocations.deleteProject);

  // Handle view project details
  const handleViewProject = (project: any) => {
    setSelectedProject(project);
    setIsProjectDetailsSheetOpen(true);
  };

  // Handle edit project
  const handleEditProject = (project: any) => {
    setSelectedProject(project);
    setIsEditProjectSheetOpen(true);
  };

  // Handle delete project
  const handleDeleteProject = async () => {
    if (!projectToDelete) return;
    setIsDeleting(true);
    try {
      await deleteProject({ projectId: projectToDelete.id });
      toast.success("Project deleted successfully");
      setProjectToDelete(null);
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    } finally {
      setIsDeleting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Check if any filters are active
  const hasActiveFilters = statusFilter !== null || searchQuery !== "";

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter(null);
    setSearchQuery("");
  };

  // Filter projects
  const filteredProjects = projects.filter((project: any) => {
    const matchesSearch =
      searchQuery === "" ||
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.locationName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.businessName.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === null || project.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
    <DashboardLayout activeTab="calendar">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Service Projects</h1>
            <p className="mt-1 text-gray-500">Schedule and manage service projects</p>
          </div>
          <div className="flex items-center gap-4">
            {/* View toggle buttons */}
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(value: "list" | "calendar") => value && setViewMode(value)}
            >
              <ToggleGroupItem value="list" aria-label="List view">
                <List className="mr-2 h-4 w-4" />
                List
              </ToggleGroupItem>
              <ToggleGroupItem value="calendar" aria-label="Calendar view">
                <CalendarIcon className="mr-2 h-4 w-4" />
                Calendar
              </ToggleGroupItem>
            </ToggleGroup>
            <Button onClick={() => setIsScheduleSheetOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" /> Add Project
            </Button>
          </div>
        </div>

        <Card className="w-full p-6">
          <div className="flex flex-col gap-4">
            {/* Search and filters row */}
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <Input
                  placeholder="Search projects by name, client, or location..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select
                value={statusFilter || "all"}
                onValueChange={(value) => setStatusFilter(value === "all" ? null : value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Scheduled">Scheduled</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="gap-2 text-gray-500 hover:text-gray-700"
                >
                  <X className="h-4 w-4" /> Clear Filters
                </Button>
              )}
            </div>
          </div>
        </Card>

        {viewMode === "calendar" ? (
          <ProjectCalendarView
            projects={filteredProjects}
            onViewProject={handleViewProject}
            onEditProject={handleEditProject}
          />
        ) : (
          /* Projects Grid - List View */
          <div className="grid grid-cols-1 gap-4">
            {filteredProjects.length === 0 ? (
              <Card className="p-8">
                <CardContent className="p-0">
                  <EmptyState
                    icon={CalendarIcon}
                    title="No projects found"
                    description={
                      hasActiveFilters
                        ? "Try adjusting your search or filters."
                        : "Get started by scheduling your first project."
                    }
                    actionLabel={hasActiveFilters ? "Clear Filters" : "Add Project"}
                    actionIcon={hasActiveFilters ? X : Plus}
                    onAction={hasActiveFilters ? clearFilters : () => setIsScheduleSheetOpen(true)}
                  />
                </CardContent>
              </Card>
            ) : (
              filteredProjects.map((project: any) => (
                <Card key={project._id} className="p-6 transition-shadow hover:shadow-md">
                  <CardContent className="p-0">
                    <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-medium">{project.name}</h3>
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(
                              project.status
                            )}`}
                          >
                            {project.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <CalendarIcon className="h-4 w-4" />
                            <span>
                              {formatDate(project.startDate)} - {formatDate(project.dueDate)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            <span>
                              {project.locationName} - {project.locationAddress}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Users className="h-4 w-4" />
                          <span>
                            {project.assignedToNames?.length ||
                            (project as any).assignedSubcontractorNames?.length ? (
                              <>
                                {project.assignedToNames?.length > 0 && (
                                  <span className="text-blue-600">
                                    Employees: {project.assignedToNames.join(", ")}
                                  </span>
                                )}
                                {project.assignedToNames?.length > 0 &&
                                  (project as any).assignedSubcontractorNames?.length > 0 &&
                                  " | "}
                                {(project as any).assignedSubcontractorNames?.length > 0 && (
                                  <span className="text-green-600">
                                    Subcontractors:{" "}
                                    {(project as any).assignedSubcontractorNames.join(", ")}
                                  </span>
                                )}
                              </>
                            ) : (
                              "No staff assigned"
                            )}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">Client: {project.businessName}</p>
                        {project.description && (
                          <p className="line-clamp-2 text-sm text-gray-500">
                            {project.description}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewProject(project)}
                          className="gap-1"
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </Button>
                        {canModifyProject(project) && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditProject(project)}
                              className="gap-1"
                            >
                              <Edit className="h-4 w-4" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setProjectToDelete({ id: project._id, name: project.name })}
                              className="gap-1"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        <ScheduleProjectSheet
          isOpen={isScheduleSheetOpen}
          onClose={() => setIsScheduleSheetOpen(false)}
          onProjectScheduled={() => {
            // Projects list will automatically refresh due to Convex reactivity
          }}
        />

        <ProjectDetailsSheet
          isOpen={isProjectDetailsSheetOpen}
          onClose={() => {
            setIsProjectDetailsSheetOpen(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
          onEdit={() => {
            // Close details sheet and open edit sheet
            setIsProjectDetailsSheetOpen(false);
            setIsEditProjectSheetOpen(true);
          }}
        />

        <EditProjectSheet
          isOpen={isEditProjectSheetOpen}
          onClose={() => {
            setIsEditProjectSheetOpen(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
          onProjectUpdated={() => {
            // Projects list will automatically refresh due to Convex reactivity
          }}
        />

        <AlertDialog
          open={!!projectToDelete}
          onOpenChange={(open) => !open && setProjectToDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure you want to delete?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the project "{projectToDelete?.name}".
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteProject}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
